import { Timer, PhoneOff, FileWarning, TrendingDown } from 'lucide-react';
import { Card, CardContent } from '../ui/card';

export const ProblemAgitation = () => {
  const problems = [
    {
      icon: Timer,
      title: "The Speed-to-Lead Gap",
      stat: "400%",
      statLabel: "drop in lead quality",
      description: "Research shows responding within 5 minutes makes leads 400% more likely to convert. Most roofing companies take 2-4 hours.",
      highlight: "Every hour of delay costs you jobs."
    },
    {
      icon: PhoneOff,
      title: "The Voicemail Abyss",
      stat: "70%",
      statLabel: "never call back",
      description: "When leads hit voicemail, 70% won't call back. They'll call the next roofer who answers. That's not a maybe—that's a stat.",
      highlight: "Your competitor is one ring away."
    },
    {
      icon: FileWarning,
      title: "The Admin Bottleneck",
      stat: "15+",
      statLabel: "hours/week wasted",
      description: "Your best sales people are buried in form follow-ups, missed call callbacks, and scheduling headaches instead of closing.",
      highlight: "You're paying closers to do data entry."
    }
  ];

  return (
    <section className="py-20 md:py-28 bg-white" data-testid="problem-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-orange-600 font-semibold text-sm uppercase tracking-wider mb-3">
            The Hidden Revenue Leak
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-6">
            Your Phone Is Ringing. Money Is Walking.
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto leading-relaxed">
            For every 100 leads you get, slow follow-up and missed calls are costing you 20-30 jobs. Here's how it happens:
          </p>
        </div>

        {/* Problem Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {problems.map((problem, index) => (
            <Card 
              key={index} 
              className="bg-slate-50 border-slate-200 rounded-sm hover:shadow-lg transition-shadow duration-300"
              data-testid={`problem-card-${index}`}
            >
              <CardContent className="p-8">
                {/* Icon */}
                <div className="w-14 h-14 rounded bg-red-100 flex items-center justify-center mb-6">
                  <problem.icon className="w-7 h-7 text-red-600" />
                </div>
                
                {/* Title */}
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-4">
                  {problem.title}
                </h3>
                
                {/* Stat */}
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="font-heading text-4xl font-bold text-red-600">
                    {problem.stat}
                  </span>
                  <span className="text-slate-500 text-sm">
                    {problem.statLabel}
                  </span>
                </div>
                
                {/* Description */}
                <p className="text-slate-600 leading-relaxed mb-4">
                  {problem.description}
                </p>
                
                {/* Highlight */}
                <p className="text-slate-800 font-semibold text-sm border-l-2 border-orange-500 pl-3">
                  {problem.highlight}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-3 bg-red-50 border border-red-200 rounded-sm px-6 py-4">
            <TrendingDown className="w-5 h-5 text-red-600" />
            <p className="text-red-800 font-medium">
              At $15,000 average job value, losing just 2 extra jobs per month = <span className="font-bold">$360,000/year in lost revenue</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemAgitation;
